import { useEffect, useState } from "react";
import { CalendarDays, MapPin, Building2, Plane, Handshake, TrainFront, DoorOpen, Globe, Phone, Mail, AlertTriangle, Sparkles, ExternalLink } from "lucide-react";
import api from "../lib/api";

/* Extensive AI-written profile article for an airline or airport.
   Generated on demand (cached server-side) via /api/{airlines|airports}/{code}/article. */
export default function AiEntityProfile({ type, code, name }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(false); setData(null);
    api.get(`/${type}s/${encodeURIComponent(code)}/article`, { timeout: 90000 })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [type, code]);

  if (loading) return (
    <div className="border border-zinc-300 bg-white p-8 flex items-center gap-4" data-testid={`ai-${type}-loading`}>
      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
      <div className="text-base text-zinc-700">We are writing the full profile for <span className="font-700 text-zinc-900">{name || `this ${type}`}</span>. This can take a few seconds…</div>
    </div>
  );
  if (error || !data) return null;

  const facts = type === "airline"
    ? [
        { icon: CalendarDays, label: "Founded", value: data.founded, color: "text-blue-600" },
        { icon: Building2, label: "Headquarters", value: data.headquarters, color: "text-violet-600" },
        { icon: Handshake, label: "Alliance", value: data.alliance && data.alliance !== "None" ? data.alliance : null, color: "text-emerald-600" },
      ]
    : [
        { icon: CalendarDays, label: "Opened", value: data.opened, color: "text-blue-600" },
        { icon: MapPin, label: "Location", value: data.location, color: "text-violet-600" },
      ];
  const sections = type === "airline"
    ? [
        { title: "Hubs & network", body: data.hubs_summary, color: "border-blue-500 text-blue-600" },
        { title: "Fleet", body: data.fleet_summary, color: "border-violet-500 text-violet-600" },
      ]
    : [
        { title: "Terminals", body: data.terminals_summary, color: "border-blue-500 text-blue-600", icon: DoorOpen },
        { title: "Getting to & from", body: data.transport_summary, color: "border-violet-500 text-violet-600", icon: TrainFront },
        { title: "Airlines here", body: data.airlines_summary, color: "border-emerald-500 text-emerald-600", icon: Plane },
      ];
  const cs = data.customer_service || {};
  const website = cs.website || data.website;

  return (
    <div className="border border-zinc-300 bg-white p-6 lg:p-8" data-testid={`ai-${type}-profile`}>
      <div className="text-xs font-mono uppercase tracking-widest text-primary flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3.5 h-3.5" /> TravelerPulse profile
      </div>
      {data.tagline && <p className="font-heading font-900 text-xl sm:text-2xl text-primary leading-snug mb-4" data-testid={`ai-${type}-tagline`}>{data.tagline}</p>}
      {facts.filter((f) => f.value).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          {facts.filter((f) => f.value).map((f) => (
            <div key={f.label} className="border-l-4 border-zinc-200 pl-3">
              <div className={`text-xs font-mono uppercase tracking-widest flex items-center gap-1 ${f.color}`}><f.icon className="w-3.5 h-3.5" />{f.label}</div>
              <div className="text-sm font-600 text-zinc-900 mt-0.5">{f.value}</div>
            </div>
          ))}
        </div>
      )}
      {data.overview && <div className="whitespace-pre-line leading-relaxed text-zinc-800 text-[16px]" data-testid={`ai-${type}-overview`}>{data.overview}</div>}
      {sections.filter((s) => s.body).map((s) => (
        <div key={s.title} className={`mt-5 border-l-4 pl-3 ${s.color.split(" ")[0]}`}>
          <div className={`font-heading font-900 text-lg ${s.color.split(" ")[1]}`}>{s.title}</div>
          <div className="text-base text-zinc-800 mt-0.5">{s.body}</div>
        </div>
      ))}
      {data.notable_facts?.length > 0 && (
        <div className="mt-5">
          <div className="font-heading font-900 text-emerald-600 text-lg mb-2">Notable facts</div>
          <ul className="space-y-1.5">
            {data.notable_facts.map((f, i) => <li key={i} className="flex gap-2 text-base text-zinc-800"><span className="text-emerald-600 font-mono font-700">›</span>{f}</li>)}
          </ul>
        </div>
      )}
      {(website || cs.phone || cs.email || data.links?.wikipedia) && (
        <div className="mt-5 flex flex-wrap gap-2">
          {website && (
            <a href={website} target="_blank" rel="noreferrer" data-testid={`ai-${type}-website`}
              className="inline-flex items-center gap-1.5 text-xs font-700 text-white bg-primary px-3 py-1.5 hover:opacity-90">
              <Globe className="w-3.5 h-3.5" /> Official website
            </a>
          )}
          {cs.phone && <span className="inline-flex items-center gap-1.5 text-xs font-600 border border-zinc-300 px-3 py-1.5"><Phone className="w-3.5 h-3.5 text-emerald-600" /> {cs.phone}</span>}
          {cs.email && <a href={`mailto:${cs.email}`} className="inline-flex items-center gap-1.5 text-xs font-600 border border-zinc-300 px-3 py-1.5 hover:border-primary"><Mail className="w-3.5 h-3.5 text-blue-600" /> {cs.email}</a>}
          {data.links?.wikipedia && (
            <a href={data.links.wikipedia} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-600 border border-zinc-300 px-3 py-1.5 hover:border-primary">
              <ExternalLink className="w-3.5 h-3.5" /> Wikipedia
            </a>
          )}
        </div>
      )}
      {data.disclaimer && (
        <div className="mt-5 flex gap-2 items-start text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{data.disclaimer}</span>
        </div>
      )}
    </div>
  );
}
